import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardResourcesSqlMap } from '../sql-maps/biviz-dashboard-resources-datasource-sql-map';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';

export interface DashboardResourcesByDayNoDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class DashboardResourcesByDayNoDataLoaderFactory extends Factory<
    DataLoader,
    DashboardResourcesByDayNoDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'DashboardResourcesByDayNoDataLoaderFactory';

    getInstance(options: DashboardResourcesByDayNoDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                // ignore columns that are not supported in this query
                const ignoredContextColumnIds = [
                    'ACTIVITY_DATE',
                    'ACTIVITY_APP_VERSION',
                    'ACTIVITY_APP_VERSION_GROUP_MINOR',
                ];
                const resourcesSqlMap = BiVizDashboardResourcesSqlMap.sqlMap;

                // identify columns not supported in datasource
                const resourcesDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        resourcesSqlMap.columns
                    );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(
                        resourcesDsColumns.missing
                    );

                //filter out ignored columns from dsColumns.present
                resourcesDsColumns.present = resourcesDsColumns.present.filter(
                    (col) => !ignoredContextColumnIds.includes(col.id)
                );

                const resourcesSQL =
                    BiVizDashboardResourcesSqlMap.sqlStatementsCompiler.compileSQLStatements(
                        resourcesDsColumns.present,
                        queryContext.settings,
                        options
                        // selectedDayNoSQL
                    );

                const usersdashActivity = BiVizDashboardUsersActivity; // identify columns not supported in datasource
                const usersdashActivityDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        usersdashActivity.sqlMap.columns
                    );
                usersdashActivityDsColumns.present =
                    usersdashActivityDsColumns.present.filter(
                        (col) => !ignoredContextColumnIds.includes(col.id)
                    );
                const usersdashActivitySQL =
                    usersdashActivity.compileSQLStatements(
                        usersdashActivityDsColumns.present,
                        queryContext.settings,
                        options
                        // selectedDayNoSQL
                    );

                const getDefaultMaxDayNo = (context: IQueryContext) => {
                    return `MAX(day_no) OVER (PARTITION BY ${usersdashActivity.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL})`;
                };

                const maxDayNo = '90';
                const allResources =
                    BiVizDashboardResourcesSqlMap.ALL_RESOURCES;
                const win = resourcesSQL.windowRunning('c');

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH dev AS
(
  SELECT *
    ${['energy', 'gems', 'hints', 'coins', 'compasses'].map((r) => `, CASE WHEN ${r}_inflow IS NULL AND ${r}_outflow IS NULL THEN NULL ELSE COALESCE(${r}_inflow, 0) - COALESCE(${r}_outflow, 0) END AS ${r}_balance`).join('\n  ')}
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      resourcesSqlMap.dataset,
      resourcesSqlMap.tableId
  )}\` ds
  -- WHERE energy_inflow <= 1000000 OR energy_inflow IS NULL -- filter out cheaters
)

, resources AS
(
  SELECT
    ${resourcesSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    *
  FROM
  (
    SELECT
      ${resourcesSQL.cohortSplitSelect}
      ${resourcesSQL.datasourceSpecificSplitSelect}
      player_day.day_no
      ${allResources
          .flatMap((r) => [
              `, SUM(${r}_inflow) AS ${r}_inflow`,
              `, SUM(${r}_outflow) AS ${r}_outflow`,
              `, SUM(${r}_balance) AS ${r}_balance`,
              `, APPROX_QUANTILES(${r}_balance, 100)[OFFSET(50)] AS median_${r}_balance_per_user`,
              `, APPROX_QUANTILES(${r}_balance, 100)[OFFSET(75)] AS p75_${r}_balance_per_user`,
              `, APPROX_QUANTILES(${r}_balance, 100)[OFFSET(90)] AS p90_${r}_balance_per_user`,
              `, APPROX_QUANTILES(${r}_inflow, 100)[OFFSET(50)] AS median_${r}_inflow_per_user`,
              `, APPROX_QUANTILES(${r}_outflow, 100)[OFFSET(50)] AS median_${r}_outflow_per_user`,
          ])
          .join('\n      ')}
    FROM dev ds
    ${resourcesSQL.join}
    ${resourcesSQL.where}
    GROUP BY ${resourcesSQL.cohortSplitGroupBy}${
        resourcesSQL.datasourceSpecificSplitGroupBy
    }day_no
  )
)


${
    // cohort base korzysta z tabeli usersdash_activity i nie ma informacji o ads_flow_split_group
    // dlatego jeśli jest w context to musimy zrobić cross join
    // jeśli nie ma w context to nie ma sensu robić cross join
    // po cross join korzystamy z adsFlowDailySQL
    ''
}
, cohort_base AS
(
    SELECT
      ${
          usersdashActivitySQL.splitGroupBy != ''
              ? `${usersdashActivitySQL.splitSelect.replace(
                    'AS split_group',
                    'AS cohort_split_group'
                )}`
              : ''
      }
      ds.*
      , ${maxDayNo} AS max_day_no
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        usersdashActivity.sqlMap.dataset,
        usersdashActivity.sqlMap.tableId
    )}\` ds
    ${usersdashActivitySQL.join}
    ${usersdashActivitySQL.where}
)

, cohort_size AS
(
  SELECT
    ${resourcesSQL.splitGroupBy}
    day_no,
    SUM(cohort_size) AS cohort_size
  FROM
  (
    SELECT
      ${resourcesSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
      ${resourcesSqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL},
      COUNT(DISTINCT player_id) AS cohort_size,
      GENERATE_ARRAY(0, max_day_no ) AS days
    FROM
    (
      SELECT cohort_base.*
      FROM cohort_base
      WHERE day_no = 0
    )
    ${
        resourcesSQL.is_in_context_split_datasource_specific_columns
            ? `CROSS JOIN (SELECT DISTINCT resources_split_group FROM resources)`
            : ''
    }
    GROUP BY ${resourcesSQL.splitGroupBy}${
        resourcesSqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL
    }, max_day_no
  ), UNNEST(days) AS day_no
  GROUP BY ${resourcesSQL.splitGroupBy}day_no
)

, by_fod AS
(
SELECT
  ${resourcesSQL.splitGroupBy != '' ? 'c.split_group,' : ''}
  c.day_no
  ${allResources
      .flatMap((r) => [
          `, ROUND(SAFE_DIVIDE(${r}_inflow, cohort_size), 3) AS avg_${r}_inflow_per_user`,
          `, ROUND(SAFE_DIVIDE(${r}_outflow, cohort_size), 3) AS avg_${r}_outflow_per_user`,
          `, ROUND(SAFE_DIVIDE(${r}_balance, cohort_size), 3) AS avg_${r}_balance_per_user`,
          `, median_${r}_balance_per_user`,
          `, p75_${r}_balance_per_user`,
          `, p90_${r}_balance_per_user`,
          `, ROUND(SUM(SAFE_DIVIDE(${r}_inflow, cohort_size)) OVER (${win}), 3) AS running_avg_${r}_inflow_per_user`,
          `, ROUND(SUM(SAFE_DIVIDE(${r}_outflow, cohort_size)) OVER (${win}), 3) AS running_avg_${r}_outflow_per_user`,
          `, ROUND(SUM(SAFE_DIVIDE(${r}_balance, cohort_size)) OVER (${win}), 3) AS running_avg_${r}_balance_per_user`,
          `, ROUND(SUM(median_${r}_balance_per_user) OVER (${win}), 3) AS running_median_${r}_balance_per_user`,
          `, ROUND(SUM(p75_${r}_balance_per_user) OVER (${win}), 3) AS running_p75_${r}_balance_per_user`,
          `, ROUND(SUM(p90_${r}_balance_per_user) OVER (${win}), 3) AS running_p90_${r}_balance_per_user`,
          `, ROUND(SUM(median_${r}_inflow_per_user) OVER (${win}), 3) AS running_median_${r}_inflow_per_user`,
          `, ROUND(SUM(median_${r}_outflow_per_user) OVER (${win}), 3) AS running_median_${r}_outflow_per_user`,
          `, b.${r}_inflow AS ${r}_inflow`,
          `, b.${r}_outflow AS ${r}_outflow`,
          `, b.${r}_balance AS ${r}_balance`,
      ])
      .join('\n  ')}

  FROM cohort_size c
  LEFT JOIN resources b
    ON c.day_no = b.day_no
    ${resourcesSQL.splitGroupBy != '' ? `AND c.split_group = b.split_group` : ''}
)

SELECT * FROM by_fod
ORDER BY ${resourcesSQL.splitGroupBy}day_no
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
